import { useState } from "react";
import {
  Card,
  Group,
  SimpleGrid,
  Text,
  Skeleton,
  Box,
  Tabs,
  Stack,
} from "@mantine/core";
import { DonutChart, AreaChart } from "@mantine/charts";
import { useQuery } from "@tanstack/react-query";
import {
  reportsApi,
  type ReportParams,
  type ReportKey,
} from "../../api/reports";
import { PageHeader } from "../../components/common/PageHeader";
import { StatCard } from "../../components/common/StatCard";
import { QueryError } from "../../components/common/QueryError";
import { DateRangePresetPicker } from "../../components/common/DateRangePresetPicker";
import { CsvDownloadButton } from "../../components/common/CsvDownloadButton";
import { DataTable, type Column } from "../../components/common/DataTable";
import { formatMoney, formatNumber } from "../../lib/format";
import { humanize } from "../../lib/labels";

// ---- exact response shapes (verified against report.repository.ts) ----
interface RevenueSummary {
  summary: {
    totalRevenue: number;
    totalTrips: number;
    totalGrossFare: number;
    totalNetFare: number;
    totalDiscount: number;
    totalDriverEarning: number;
    totalPlatformCommission: number;
    totalBookingFee: number;
    totalLatraLevy: number;
    totalPlatformEarning: number;
    totalVAT: number;
    totalPaymentGatewayFee: number;
  };
}
interface PaymentDistribution {
  distribution: { paymentMode: string; count: number; totalAmount: number; percentage: number }[];
}
interface TransactionStatusReport {
  statusReport: { status: string; count: number; totalAmount: number; percentage: number }[];
}
interface GatewayAnalysis {
  gatewayAnalysis: {
    totalGatewayFees: number;
    totalOnlineRevenue: number;
    gatewayFeePercentage: number;
    transactionCount: number;
  };
}
interface DailyRevenue {
  dailyRevenue: { date: string; totalRevenue: number; totalTrips: number }[];
}
interface DriverEarnings {
  earnings: {
    driverId: string;
    driverName: string;
    driverPhone: string;
    totalEarnings: number;
    totalTrips: number;
    currentDebt: number;
  }[];
}
interface DriverDebt {
  debtReport: {
    driverId: string;
    driverName: string;
    driverPhone: string;
    debtAmount: number;
    isLocked: boolean;
  }[];
}
interface CouponImpact {
  couponImpact: {
    couponCode: string;
    timesUsed: number;
    totalDiscountAmount: number;
    totalRevenueImpact: number;
  }[];
}

// Categorical palette, fixed order (a 7th series would fold into "Other").
const DONUT_COLORS = [1, 2, 3, 4, 5, 6].map((n) => `var(--viz-cat-${n})`);

function useReport<T>(key: ReportKey, params: ReportParams) {
  return useQuery({
    queryKey: ["report", key, params],
    queryFn: () => reportsApi.get<T>(key, params),
  });
}

export function ReportsPage() {
  const [params, setParams] = useState<ReportParams>({ preset: "last_30_days" });

  return (
    <>
      <PageHeader
        title="Reports"
        subtitle="Financial & operational reporting"
        actions={<DateRangePresetPicker value={params} onChange={setParams} />}
      />

      <RevenueKpis params={params} />

      <Tabs defaultValue="revenue" mt="lg">
        <Tabs.List mb="md">
          <Tabs.Tab value="revenue">Revenue</Tabs.Tab>
          <Tabs.Tab value="payments">Payments</Tabs.Tab>
          <Tabs.Tab value="drivers">Drivers</Tabs.Tab>
          <Tabs.Tab value="marketing">Marketing</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="revenue">
          <DailyRevenuePanel params={params} />
        </Tabs.Panel>
        <Tabs.Panel value="payments">
          <PaymentsPanel params={params} />
        </Tabs.Panel>
        <Tabs.Panel value="drivers">
          <DriversPanel params={params} />
        </Tabs.Panel>
        <Tabs.Panel value="marketing">
          <MarketingPanel params={params} />
        </Tabs.Panel>
      </Tabs>
    </>
  );
}

function RevenueKpis({ params }: { params: ReportParams }) {
  const { data, isLoading, error, refetch } = useReport<RevenueSummary>(
    "revenueSummary",
    params,
  );
  if (error)
    return (
      <Box mb="lg">
        <QueryError error={error} onRetry={refetch} />
      </Box>
    );
  const s = data?.summary;
  return (
    <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
      {isLoading ? (
        Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} h={120} radius="md" />)
      ) : (
        <>
          <StatCard label="Total revenue" value={formatMoney(s?.totalRevenue)} />
          <StatCard label="Trips" value={formatNumber(s?.totalTrips)} />
          <StatCard label="Platform earnings" value={formatMoney(s?.totalPlatformEarning)} />
          <StatCard label="Driver earnings" value={formatMoney(s?.totalDriverEarning)} />
        </>
      )}
    </SimpleGrid>
  );
}

function ChartCard({
  title,
  reportKey,
  params,
  children,
}: {
  title: string;
  reportKey: ReportKey;
  params: ReportParams;
  children: React.ReactNode;
}) {
  return (
    <Card withBorder radius="md" padding="lg">
      <Group justify="space-between" mb="md">
        <Text fw={600}>{title}</Text>
        <CsvDownloadButton reportKey={reportKey} params={params} label="CSV" />
      </Group>
      {children}
    </Card>
  );
}

function DailyRevenuePanel({ params }: { params: ReportParams }) {
  const { data, isLoading, error, refetch } = useReport<DailyRevenue>("dailyRevenue", params);
  const rows = data?.dailyRevenue ?? [];
  return (
    <ChartCard title="Daily revenue" reportKey="dailyRevenue" params={params}>
      {error ? (
        <QueryError error={error} onRetry={refetch} />
      ) : isLoading ? (
        <Skeleton h={260} />
      ) : rows.length === 0 ? (
        <Text c="dimmed" size="sm">No data for this period.</Text>
      ) : (
        <AreaChart
          h={260}
          data={rows.map((r) => ({ date: r.date, Revenue: r.totalRevenue }))}
          dataKey="date"
          series={[{ name: "Revenue", color: "var(--viz-cat-1)" }]}
          curveType="monotone"
          valueFormatter={(v) => formatMoney(v)}
          withDots={false}
        />
      )}
    </ChartCard>
  );
}

function PaymentsPanel({ params }: { params: ReportParams }) {
  const dist = useReport<PaymentDistribution>("paymentDistribution", params);
  const txn = useReport<TransactionStatusReport>("transactionStatus", params);
  const gw = useReport<GatewayAnalysis>("gatewayFees", params);

  return (
    <Stack>
      <SimpleGrid cols={{ base: 1, md: 2 }}>
        <ChartCard title="Payment methods" reportKey="paymentDistribution" params={params}>
          {dist.isLoading ? (
            <Skeleton h={200} />
          ) : (
            <Group justify="center">
              <DonutChart
                size={200}
                thickness={28}
                withLabelsLine
                data={(dist.data?.distribution ?? []).map((d, i) => ({
                  name: humanize(d.paymentMode),
                  value: d.totalAmount,
                  color: DONUT_COLORS[i % DONUT_COLORS.length],
                }))}
                valueFormatter={(v) => formatMoney(v)}
              />
            </Group>
          )}
        </ChartCard>

        <ChartCard title="Transaction status" reportKey="transactionStatus" params={params}>
          {txn.isLoading ? (
            <Skeleton h={200} />
          ) : (
            <Group justify="center">
              <DonutChart
                size={200}
                thickness={28}
                data={(txn.data?.statusReport ?? []).map((d, i) => ({
                  name: humanize(d.status),
                  value: d.count,
                  color: DONUT_COLORS[i % DONUT_COLORS.length],
                }))}
              />
            </Group>
          )}
        </ChartCard>
      </SimpleGrid>

      <ChartCard title="Payment gateway fees" reportKey="gatewayFees" params={params}>
        {gw.isLoading ? (
          <Skeleton h={90} />
        ) : (
          <SimpleGrid cols={{ base: 2, md: 4 }}>
            <StatCard label="Gateway fees" value={formatMoney(gw.data?.gatewayAnalysis.totalGatewayFees)} />
            <StatCard label="Online revenue" value={formatMoney(gw.data?.gatewayAnalysis.totalOnlineRevenue)} />
            <StatCard label="Fee %" value={`${(gw.data?.gatewayAnalysis.gatewayFeePercentage ?? 0).toFixed(2)}%`} />
            <StatCard label="Transactions" value={formatNumber(gw.data?.gatewayAnalysis.transactionCount)} />
          </SimpleGrid>
        )}
      </ChartCard>
    </Stack>
  );
}

function DriversPanel({ params }: { params: ReportParams }) {
  const earn = useReport<DriverEarnings>("driverEarnings", params);
  const debt = useReport<DriverDebt>("driverDebt", params);

  const earnCols: Column<DriverEarnings["earnings"][number]>[] = [
    { key: "name", header: "Driver", render: (r) => r.driverName || "—" },
    { key: "phone", header: "Phone", render: (r) => r.driverPhone || "—" },
    { key: "trips", header: "Trips", align: "right", render: (r) => formatNumber(r.totalTrips) },
    { key: "earn", header: "Earnings", align: "right", render: (r) => formatMoney(r.totalEarnings) },
    { key: "debt", header: "Debt", align: "right", render: (r) => formatMoney(r.currentDebt) },
  ];
  const debtCols: Column<DriverDebt["debtReport"][number]>[] = [
    { key: "name", header: "Driver", render: (r) => r.driverName || "—" },
    { key: "phone", header: "Phone", render: (r) => r.driverPhone || "—" },
    { key: "debt", header: "Debt", align: "right", render: (r) => formatMoney(r.debtAmount) },
    { key: "locked", header: "Locked", render: (r) => (r.isLocked ? "Yes" : "No") },
  ];

  return (
    <Stack>
      <ChartCard title="Driver earnings" reportKey="driverEarnings" params={params}>
        <DataTable
          columns={earnCols}
          rows={earn.data?.earnings ?? []}
          rowKey={(r) => r.driverId}
          loading={earn.isLoading}
          emptyLabel="No driver earnings for this period"
        />
      </ChartCard>
      <ChartCard title="Driver debt" reportKey="driverDebt" params={params}>
        <DataTable
          columns={debtCols}
          rows={debt.data?.debtReport ?? []}
          rowKey={(r) => r.driverId}
          loading={debt.isLoading}
          emptyLabel="No outstanding driver debt"
        />
      </ChartCard>
    </Stack>
  );
}

function MarketingPanel({ params }: { params: ReportParams }) {
  const { data, isLoading } = useReport<CouponImpact>("couponImpact", params);
  const cols: Column<CouponImpact["couponImpact"][number]>[] = [
    { key: "code", header: "Coupon", render: (r) => r.couponCode },
    { key: "used", header: "Times used", align: "right", render: (r) => formatNumber(r.timesUsed) },
    { key: "disc", header: "Total discount", align: "right", render: (r) => formatMoney(r.totalDiscountAmount) },
    { key: "rev", header: "Revenue impact", align: "right", render: (r) => formatMoney(r.totalRevenueImpact) },
  ];
  return (
    <ChartCard title="Coupon impact" reportKey="couponImpact" params={params}>
      <DataTable
        columns={cols}
        rows={data?.couponImpact ?? []}
        rowKey={(r) => r.couponCode}
        loading={isLoading}
        emptyLabel="No coupon usage for this period"
      />
    </ChartCard>
  );
}
